import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, Spinner } from "./icons";

interface Props {
  onUninstall: () => Promise<void>;
  onSuccess?: () => void;
  label?: string;
}

export default function AgentUninstallButton({ onUninstall, onSuccess, label }: Props) {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "confirm" | "uninstalling" | "done" | "error">(
    "idle",
  );

  const displayLabel = label || t("deviceInstaller.uninstall");

  const handleUninstall = async () => {
    setState("uninstalling");
    try {
      await onUninstall();
      setState("done");
      onSuccess?.();
      setTimeout(() => setState("idle"), 2000);
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 3000);
    }
  };

  if (state === "idle") {
    return (
      <button
        onClick={() => setState("confirm")}
        className="text-xs text-gray-400 hover:text-red-500 px-2 py-1 rounded transition-colors"
      >
        {displayLabel}
      </button>
    );
  }
  if (state === "confirm") {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-red-500">{t("deviceInstaller.uninstallConfirm")}</span>
        <button
          onClick={handleUninstall}
          className="text-xs text-white bg-red-500 hover:bg-red-600 px-2 py-1 rounded transition-colors"
        >
          {t("general.confirm")}
        </button>
        <button
          onClick={() => setState("idle")}
          className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded transition-colors"
        >
          {t("general.cancel")}
        </button>
      </div>
    );
  }
  if (state === "uninstalling") {
    return (
      <div className="flex items-center gap-1.5">
        <Spinner className="w-3.5 h-3.5" color="gray" />
        <span className="text-xs text-red-500">{t("deviceInstaller.uninstalling")}</span>
      </div>
    );
  }
  if (state === "done") {
    return (
      <div className="flex items-center gap-1.5">
        <CheckIcon className="w-3.5 h-3.5 text-green-500" />
        <span className="text-xs text-green-500">{t("deviceInstaller.uninstallSuccess")}</span>
      </div>
    );
  }
  return <span className="text-xs text-red-500">{t("deviceInstaller.uninstallError")}</span>;
}
